import type { AgentSession } from '../agents/types';

const now = Date.now();
const m = (mins: number) => now - mins * 60_000;

export const MOCK_SESSIONS: AgentSession[] = [
  {
    id: 's_demo_claude',
    name: 'Loom 23 MCP',
    agentKind: 'claude',
    providerId: 'anthropic',
    modelId: 'claude-opus-4-7',
    effort: 'high',
    status: 'idle',
    availableTools: ['read_file', 'write_file', 'run_command', 'grep'],
    attachments: [],
    metadata: { project: 'Loom' },
    createdAt: m(45),
    updatedAt: m(2),
    messages: [
      {
        id: 'msg_u1',
        role: 'user',
        createdAt: m(15),
        parts: [
          {
            type: 'text',
            text: 'Audit the new Loom plan-review gate. Tell me what files I need to touch to add an `auto_approve` flag for low-risk tasks, and outline the rollout.',
          },
        ],
      },
      {
        id: 'msg_a1',
        role: 'assistant',
        model: 'claude-opus-4-7',
        effort: 'high',
        createdAt: m(14),
        trailingStatus: 'Updated todos · used 2 tools',
        parts: [
          {
            type: 'reasoning',
            text:
              "User wants the smallest correct change. Plan-review is set in `gates.ts` — I should grep for openGate and check the matrix in `review-policy.ts`. Then look at the `task.update` flow to see where the gate decision is made on status transitions. The flag should live on the task or on the scope; scope-level is more flexible.",
          },
          {
            type: 'tool-call',
            toolCall: {
              id: 'tc_1',
              name: 'grep',
              status: 'success',
              startedAt: m(14),
              endedAt: m(14),
              input: { pattern: 'openGate|review-policy' },
              output:
                'packages/api/src/gates.ts:42\npackages/api/src/review-policy.ts:18\npackages/api/src/tasks/update.ts:117',
            },
          },
          {
            type: 'tool-call',
            toolCall: {
              id: 'tc_2',
              name: 'read_file',
              status: 'success',
              startedAt: m(14),
              endedAt: m(13),
              input: { path: 'packages/api/src/review-policy.ts' },
              output:
                "// review-policy.ts (excerpt)\nexport function shouldOpenPlanGate(t: Task) {\n  return matrix[t.risk][t.complexity].includes('plan');\n}",
            },
          },
          {
            type: 'text',
            text: `## Plan

Three files need touching to add a scope-level \`autoApproveLowRisk\` flag.

### Files
| File | Change |
| --- | --- |
| \`packages/api/src/review-policy.ts\` | Branch on \`scope.autoApproveLowRisk\` before consulting the matrix. |
| \`packages/api/src/gates.ts\` | When auto-approving, write the resolution event so audit log stays intact. |
| \`packages/db/prisma/schema.prisma\` | Add \`autoApproveLowRisk Boolean @default(false)\` to \`Scope\`. |

### Logic

\`\`\`ts
export function shouldOpenPlanGate(t: Task, scope: Scope) {
  if (scope.autoApproveLowRisk && t.risk === 'low' && t.complexity !== 'large') {
    return false;
  }
  return matrix[t.risk][t.complexity].includes('plan');
}
\`\`\`

### Rollout
1. Ship the schema migration with the flag default-off.
2. Add the runtime check (PR #2).
3. Backfill: opt in only the *Loom* scope first.

See [Loom review gate matrix](https://example.com/loom-gates) for context.`,
          },
          {
            type: 'tool-call',
            toolCall: {
              id: 'tc_3',
              name: 'write_file',
              status: 'requires_approval',
              startedAt: m(13),
              input: {
                path: 'packages/api/src/review-policy.ts',
                preview:
                  '+  if (scope.autoApproveLowRisk && t.risk === "low" && t.complexity !== "large") {\n+    return false;\n+  }',
              },
              approvalReason:
                'Modifies review policy — affects every scope. Confirm before applying.',
            },
          },
          {
            type: 'tool-call',
            toolCall: {
              id: 'tc_4',
              name: 'run_tests',
              status: 'running',
              startedAt: m(2),
              input: { target: 'packages/api' },
            },
          },
        ],
      },
    ],
  },
  {
    id: 's_demo_codex',
    name: 'Refactor: extract auth middleware',
    agentKind: 'codex',
    providerId: 'codex',
    modelId: 'codex-default',
    effort: 'medium',
    status: 'idle',
    availableTools: ['read_file', 'write_file', 'run_command'],
    attachments: [],
    metadata: {},
    createdAt: m(120),
    updatedAt: m(60),
    messages: [],
  },
  {
    id: 's_demo_research',
    name: 'Eval: assistant-ui vs custom',
    agentKind: 'research',
    providerId: 'anthropic',
    modelId: 'claude-sonnet-4-6',
    effort: 'medium',
    status: 'idle',
    availableTools: ['web_search', 'read_url'],
    attachments: [],
    metadata: {},
    createdAt: m(360),
    updatedAt: m(300),
    messages: [],
  },
];
